import { useContext, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRouteMatch } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { PendingStatus } from '@components/dataset'
import { submissionContext } from '@contexts/submission-context'
import { useSettings } from '@datasets/dataset-details/hooks/useSettings'
import { ClassificationRowStatus } from '@global/api/types'
import { useCreativeClassifications } from '@global/hooks/queries/useCreativeClassifications'
import {
  DEFAULT_PARAMS,
  KEY as DATASETS_QUERY_KEY,
} from '@global/hooks/queries/useDatasetsDataQuery'
import { KEY as DATASETS_QUERY_OBSERVER_KEY } from '@global/hooks/queries/useDatasetsDataQueryObserver'
import { useAppSelector } from '@redux/hooks'
import { useClassificationRowStatusQuery } from './queries/useClassificationRowStatusQuery'
import { useTemplate } from './useTemplate'

interface IArgs {
  datasetId: string
  refetchInterval?: number | null
  isQueryDefaultEnabled?: boolean
}

export const Status = {
  FAILED: 'FAILED',
  WAITING_TO_BE_PROCESSED: 'WAITING_TO_BE_PROCESSED',
  RETRYING: 'RETRYING',
  UNCLASSIFIED: 'UNCLASSIFIED',
} as const

export type Status = (typeof Status)[keyof typeof Status]

function groupByStatus(rows: ClassificationRowStatus[]) {
  const initialResult: {
    failed: ClassificationRowStatus[]
    processing: ClassificationRowStatus[]
  } = {
    failed: [],
    processing: [],
  }

  return rows.reduce((result, row) => {
    if (row.requestStatus === Status.FAILED) {
      result.failed.push(row)
      return result
    }

    // Since the query doesn't return 'DONE' status, we can assume that the row is processing
    // if the request status is not 'FAILED'
    result.processing.push(row)
    return result
  }, initialResult)
}

/**
 * Custom hook to get the classification row status.
 * We divide the rows into failed and processing based on the request status for performance.
 * The hook will return the failed and processing rows
 * based on the dataset id, refetch interval and the enabled flag.
 *
 * @param {string} datasetId - The dataset id
 * @param {number} refetchInterval - The interval to refetch the data
 * @param {boolean} shouldFetchStatus - The flag to enable/disable the useQuery hook
 * @param {function} onDisableQuery - The callback function to call when the query is disabled
 * @returns {Object} Contains the failed and processing rows
 * @example
 * const { failed, processing } = useClassificationRowStatus({ datasetId, refetchInterval, enabled });
 * console.log('failed', failed, 'processing', processing);
 *
 * // failed [{...}], processing [{...}]
 *
 */

interface ClassificationRowStatusRouterParams {
  id: string
}

export const useClassificationRowStatus = ({
  datasetId,
  refetchInterval = 3000,
  isQueryDefaultEnabled = false,
}: IArgs) => {
  const previousData = useRef<ClassificationRowStatus[]>([])
  const match = useRouteMatch<ClassificationRowStatusRouterParams>()
  const { id } = match.params
  const { byId } = useContext(submissionContext)
  const submission = useMemo(() => byId[id], [id, byId])
  const { data: classifications = [] } = useCreativeClassifications()
  const [APPROVAL_ENABLED = false, CLASSIFICATIONS_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.APPROVAL, FeatureDict.CREATIVE_CLASSIFICATION]),
  )

  const dataset = useAppSelector(
    state => state.Datasets.datasetMetadataById[submission?.dataset_id],
  )
  const { template } = useTemplate({ templateId: dataset?.template_id })

  const templateHasCreativeConfiguration = (template?.creativeConfiguration?.length || 0) > 0

  const { requireApproval } = useSettings()
  const queryClient = useQueryClient()

  function invalidateDatasetQueries() {
    const hasCommentSupport = APPROVAL_ENABLED && requireApproval
    /**
     * When the query is disabled, we need to refetch the dataset and invalidate the dataset and observer queries
     * to make sure the data is up to date.
     * refetchDatasetNum and refetchDatasetRefNum are used to force the refetch of the dataset and observer queries,
     * and they're hardcoded because the value is always gonna be 1 for this workflow, we need to invalidate the query
     * called in useDatasetsQuery.ts where these values are always one.
     */
    void queryClient.invalidateQueries({
      queryKey: [
        DATASETS_QUERY_KEY,
        {
          ...DEFAULT_PARAMS,
          datasetId: id,
          hasCommentSupport,
          refetchDatasetNum: 1,
          refetchDatasetRefNum: 1,
          classificationCategoryIds: classifications.map(({ id }) => id),
        },
      ],
    })

    void queryClient.invalidateQueries({ queryKey: [DATASETS_QUERY_OBSERVER_KEY] })
  }

  /**
   * Enable row status query when we don't have classifications and the submission status
   * is awaiting approval, error or paused. We don't need to query the row status when the
   * submission is completed or in progress. If we have classifications, we don't need
   * to query the row status, because we already have the data.
   * For submissions that are not in the pending status, we don't need to query the row status,
   * because we wait till everything is completed to show the dataset.
   */
  const shouldFetchStatus =
    CLASSIFICATIONS_ENABLED &&
    templateHasCreativeConfiguration &&
    isQueryDefaultEnabled &&
    (submission?.status === PendingStatus.AWAITING_APPROVAL ||
      submission?.status === PendingStatus.ERROR ||
      submission?.status === PendingStatus.PAUSED)

  const { data = [], isFetched } = useClassificationRowStatusQuery({
    datasetId,
    refetchInterval,
    enabled: shouldFetchStatus,
  })

  /**
   * If the previous data has rows and the current data has less rows, it means that
   * some rows were processed and because of that we invalidate the dataset queries
   * to show the latest changes in the dataset.
   */
  const hasProcessedRows =
    isFetched && previousData.current.length > 0 && data.length < previousData.current.length

  /**
   * If all rows failed or all rows are processed, we disable the query
   * to prevent unnecessary refetching of the data.
   * We know that all rows are processed if the previous data has rows and the current data is empty.
   */
  const allRowsFailed =
    isFetched && data.length > 0 && data.every(row => row.requestStatus === Status.FAILED)
  const allRowsProcessed = isFetched && !data.length && previousData.current?.length > 0

  if (hasProcessedRows || allRowsProcessed || allRowsFailed) {
    invalidateDatasetQueries()
  }

  previousData.current = data

  const { failed, processing } = useMemo(() => groupByStatus(data), [data])

  return {
    failed,
    processing,
  }
}
